/**************************************************************************************************/
/*   File: /src/app/stores/actions/creation/creation.actions.ts                                       */
/*   Project: newplan-frontend                                                                    */
/*   Created Date: Tuesday February 19th 2019                                                     */
/*   Author: Andrea Zamboni andrea.zamboni@trilogis.it                                            */
/*   -----                                                                                        */
/*   Last Modified: Wed May 15 2019                                                               */
/*   Modified By: Andrea Zamboni                                                                  */
/*   -----                                                                                        */
/*   Copyright (c) 2019 Trilogis s.r.l. for SIAG                                                  */
/**************************************************************************************************/
import { Action } from '@ngrx/store';

import * as fromShared from '../../../shared';

/**
 * The dossier creation action types enum
 */
export enum DossierCreationActionsType {
  SELECT_PLAN_TYPE = '[Creation] Select plan type',
  ADD_AMBITO = '[Creation] Add an ambito',
  REMOVE_AMBITO = '[Creation] Remove an ambito',
  SELECT_GEO_FILE_TYPE = '[Creation] Select geo file type',

  CREATE_DOSSIER = '[Creation] Create dossier',
  CREATE_DOSSIER_SUCCESS = '[Creation] Create dossier success',
  CREATE_DOSSIER_FAIL = '[Creation] Create dossier fail',

  SET_IS_GEOMETRIC_CHANGE = '[Creation] Set is geometric change',
  SET_IS_NORME_CHANGE = '[Creation] Set is norme change',

  RESET_DOSSIER = '[Creation] Reset dossier',

  LOAD_AMBITI_BY_PLAN = '[Creation] Load ambiti by plan',
  LOAD_AMBITI_BY_PLAN_SUCCESS = '[Creation] Load ambiti by plan success',
  LOAD_AMBITI_BY_PLAN_FAIL = '[Creation] Load ambiti by plan fail'
}

/**
 * Select plan type
 */
export class SelectPlanType implements Action {
  /**
   * The type
   */
  readonly type = DossierCreationActionsType.SELECT_PLAN_TYPE;

  /**
   * Construct the select plan type
   * @param payload The planType code
   */
  constructor(public payload: { planCode: string }) { }
}

/**
 * Add ambito
 */
export class AddAmbito implements Action {
  /**
   * The type
   */
  readonly type = DossierCreationActionsType.ADD_AMBITO;

  /**
   * Construct the add ambito
   * @param payload The ambito code
   */
  constructor(public payload: { ambitoCode: string }) { }
}
/**
 * Remove ambtio
 */
export class RemoveAmbito implements Action {
  /**
   * The type
   */
  readonly type = DossierCreationActionsType.REMOVE_AMBITO;

  /**
   * Construct the add ambito
   * @param payload the payload containing the ambito code
   */
  constructor(public payload: { ambitoCode: string }) { }
}
/**
 * Select geo file type
 */
export class SelectGeoFileType implements Action {
  /**
   * The type
   */
  readonly type = DossierCreationActionsType.SELECT_GEO_FILE_TYPE;

  /**
   * Construct the select fileType
   * @param payload The spatial data type enum
   */
  constructor(public payload: { defaultSpatialDataType: fromShared.enums.SpatialDataTypeEnum }) { }
}
/**
 * Create the dossier action
 */
export class CreateDossier implements Action {
  /**
   * The type of action
   */
  readonly type = DossierCreationActionsType.CREATE_DOSSIER;
  /**
   * The construtor for create dossier
   * @param payload the payload containing the dossier content
   */
  constructor(public payload: { dossierContent: fromShared.models.DossierContent }) { }
}
/**
 * Create the dossier action success
 */
export class CreateDossierSuccess implements Action {
  /**
   * The type of action
   */
  readonly type = DossierCreationActionsType.CREATE_DOSSIER_SUCCESS;
  /**
   * The construtor for create dossier, the id of the dossier
   * @param payload the payload containing the dossier id
   */
  constructor(public payload: { dossierId: fromShared.models.DossierId }) { }
}
/**
 * Create the dossier action fail
 */
export class CreateDossierFail implements Action {
  /**
   * The type of action
   */
  readonly type = DossierCreationActionsType.CREATE_DOSSIER_FAIL;
  /**
   * The construtor for create dossier, a message
   * @param payload the payload containing the message
   */
  constructor(public payload: { message: fromShared.models.Message }) { }
}
/**
 * Set geometric change
 */
export class SetIsSpatialDossier implements Action {
  /**
   * The type
   */
  readonly type = DossierCreationActionsType.SET_IS_GEOMETRIC_CHANGE;

  /**
   * The constructor
   * @param payload containing is geometric change
   */
  constructor(public payload: { isGeometric: boolean }) {
  }
}
/**
 * Set norme change
 */
export class SetIsRegulationDossier implements Action {
  /**
   * The type
   */
  readonly type = DossierCreationActionsType.SET_IS_NORME_CHANGE;

  /**
   * The constructor
   * @param payload containing is norme change
   */
  constructor(public payload: { isNorme: boolean }) {
  }
}
/**
 * Reset dossier
 */
export class ResetDossier implements Action {
  /**
   * The type
   */
  readonly type = DossierCreationActionsType.RESET_DOSSIER;

  /**
   * The constructor
   * @param payload containing is norme change
   */
  constructor() {
  }
}
/**
 * Load ambiti by plan
 */
export class LoadAmbitiByPlan implements Action {
  /**
  * The type
  */
  readonly type = DossierCreationActionsType.LOAD_AMBITI_BY_PLAN;

  /**
   * The constructor
   * @param payload Payload containing plancode
   */
  constructor(public payload: { planCode: string }) { }
}
/**
* Load ambiti by plan success
*/
export class LoadAmbitiByPlanSuccess implements Action {
  /**
  * The type
  */
  readonly type = DossierCreationActionsType.LOAD_AMBITI_BY_PLAN_SUCCESS;

  /**
   * The constructor
   * @param payload the ambiti array
   */
  constructor(public payload: { ambiti: fromShared.models.Ambito[] }) { }
}

/**
* Load ambiti by plan fail
*/
export class LoadAmbitiByPlanFail implements Action {
  /**
  * The type
  */
  readonly type = DossierCreationActionsType.LOAD_AMBITI_BY_PLAN_FAIL;

  /**
   * The constructor
   * @param payload containing the message
   */
  constructor(public payload: { message: fromShared.models.Message }) { }
}

/**
 * The dossier actions type
 */
export type DossierCreationActions =
  SelectPlanType |
  AddAmbito |
  RemoveAmbito |
  CreateDossier |
  CreateDossierSuccess |
  CreateDossierFail |
  SetIsSpatialDossier |
  SetIsRegulationDossier |
  ResetDossier |
  LoadAmbitiByPlan |
  LoadAmbitiByPlanSuccess |
  LoadAmbitiByPlanFail;